import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Beaker, Search, Calendar as CalendarIcon, Loader2, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/crm`;

// Same defensive date normalizer used in LeadDetail — covers legacy Excel
// serial numbers in sample_date until the backfill endpoint is run.
const formatSampleDate = (raw) => {
  if (raw === null || raw === undefined) return '—';
  const s = String(raw).trim();
  if (!s) return '—';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const num = Number(s);
  if (Number.isFinite(num) && num >= 20000 && num <= 80000) {
    const ms = (num * 86400 * 1000) + Date.UTC(1899, 11, 30);
    const d = new Date(ms);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) {
    const [, a, b, y] = m;
    return `${y}-${b.padStart(2,'0')}-${a.padStart(2,'0')}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return s;
};

export default function Samples() {
  const [samples, setSamples] = useState([]);
  const [search, setSearch] = useState("");
  const [days, setDays] = useState("all");
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const fetchSamples = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (search.trim()) params.append("search", search.trim());
      if (days !== "all") params.append("days", days);
      if (agentId !== "all") params.append("agent_id", agentId);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const r = await axios.get(`${API}/samples?${params.toString()}`);
      setSamples(r.data?.samples || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load samples");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/lead-management/agents`);
        setAgents(r.data?.agents || []);
      } catch (_e) {/* not all roles have access */}
    })();
  }, []);

  useEffect(() => {
    fetchSamples();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, agentId, fromDate, toDate]);

  return (
    <div className="p-6 space-y-6" data-testid="samples-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Beaker className="w-6 h-6 text-blue-600" /> Samples
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Every sample pushed into the CRM — across leads, agents and dates.</p>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search company, product, LFB ID, phone, GST…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchSamples()}
                className="pl-9"
                data-testid="samples-search"
              />
            </div>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="samples-days"><SelectValue placeholder="Time range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            {agents.length > 0 && (
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger data-testid="samples-agent"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agents</SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-2">
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} placeholder="From" />
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} placeholder="To" />
            </div>
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setSearch(""); setDays("all"); setAgentId("all"); setFromDate(""); setToDate(""); }}>Clear</Button>
            <Button onClick={fetchSamples}><Search className="w-4 h-4 mr-2" />Search</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">
            {samples.length} sample{samples.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            </div>
          ) : samples.length === 0 ? (
            <div className="py-12 text-center text-slate-400">No samples found for the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Sample Date</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Company</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Product Name</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">LFB ID</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Phone / GST</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Lead Owner</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Initiated By</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Source</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {samples.map((s) => (
                    <tr key={s.sample_id} className="hover:bg-slate-50" data-testid={`sample-row-${s.sample_id}`}>
                      <td className="py-2 px-3 text-slate-700">{formatSampleDate(s.sample_date)}</td>
                      <td className="py-2 px-3 font-medium text-slate-900">{s.company_name || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{s.product_name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-blue-600">{s.lfb_id || "—"}</td>
                      <td className="py-2 px-3 text-slate-500 text-xs">{s.phone || s.gst_number || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{s.agent_name || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{s.created_by_name || "—"}</td>
                      <td className="py-2 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${s.source === "bulk_upload" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                          {s.source === "bulk_upload" ? "Bulk Upload" : "Manual"}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <Link to={`/leads/${s.lead_id}`} className="text-blue-600 hover:underline" title="Open lead">
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
